import { useVModel as useVueUseVModel } from '@vueuse/core'
import { omit } from 'lodash-es'
import {
  computed,
  getCurrentInstance,
  reactive,
  ref,
  type ComponentPublicInstance,
  type defineEmits,
} from 'vue'
import type { ValidationValue } from '../../components/OenUiInputValidation/types'
import { useComponentId } from '../useComponentId/index'
import { useMergeValidationHelper } from '../useMergeValidationHelper/index'
import { useValidationHelpers } from '../useValidationHelpers/index'

const INPUT_CLASS_CONST = 'input-class'

export function useInputHelpers(
  instance = getCurrentInstance(),
  $props: Record<string, unknown> = {}
) {
  // Make some reactive instance parts
  const event = instance?.proxy?.$options.modelValue?.event || 'input'
  const attrs = computed<Record<string, unknown>>(() => {
    const filtered = omit(instance?.proxy?.$attrs, event)
    return {
      ...filtered,
    }
  })

  const props = computed<ComponentPublicInstance | null | undefined>(() => instance?.proxy)
  const localProps = computed(() => $props)
  const emit = instance?.emit

  const validation = useMergeValidationHelper(
    props.value,
    props.value?.validation as ValidationValue<unknown>,
    localProps.value.componentValidation as ValidationValue<unknown>
  )

  const validationHelpers = useValidationHelpers(validation)

  // Root
  const rootClasses = computed(() => {
    return {
      'oen-ui-input': true,
      'oen-ui-input--disabled': props.value?.disabled || attrs.value.disabled,
      'oen-ui-input--readonly': props.value?.readonly || attrs.value.readonly,
      'oen-ui-input--error': validationHelpers?.hasError,
      'oen-ui-input--valid': validationHelpers?.isValid,
      'oen-ui-input--inline':
        props?.value?.inlineLabel || attrs.value['inline-label'] || attrs.value.inlineLabel,
      ...(attrs.value.class ? { [attrs.value.class as string]: true } : {}),
    }
  })

  // Wrapper
  const wrapperClasses = ['oen-ui-input-wrapper']

  // Label
  const labelId = useComponentId('label')
  const labelClasses = ['oen-ui-input-label']
  const labelText = computed(() => {
    if (localProps.value.componentLabel) {
      return localProps.value.componentLabel
    }

    return props.value.label
  })

  // Input
  const inputId = computed(() => {
    return useComponentId('input').value
  })
  const inputClasses = computed(() => [
    'oen-ui-input-input',
    attrs.value[INPUT_CLASS_CONST] ? attrs.value[INPUT_CLASS_CONST] : '',
  ])
  const inputAttrs = computed(() => {
    return {
      ...omit(attrs.value, 'class', INPUT_CLASS_CONST),
      id: inputId.value,
      'aria-labelledby': labelId.value,
      'aria-invalid': validationHelpers.ariaInvalid?.value,
      'aria-describedby': validationHelpers.ariaDescribedBy?.value,
      'analytics-name': props.value?.analyticsName ? props.value.analyticsName : null,
    }
  })

  const modelValue = useVueUseVModel(props.value, 'modelValue', emit)

  const root = reactive({
    classes: rootClasses,
  })
  const wrapper = reactive({
    classes: wrapperClasses,
    validation,
  })
  const label = reactive({
    id: labelId,
    classes: labelClasses,
    text: labelText,
  })
  const input = reactive({
    id: inputId,
    classes: inputClasses,
    attrs: inputAttrs,
    modelValue,
  })
  return reactive({
    // Helper
    root,
    wrapper,
    label,
    input,
    validationHelpers,
    // Component
    props,
    attrs,
    emit,
  })
}

type VueEmit = ReturnType<typeof defineEmits>
/**
 * @deprecated useVModel will be removed and should be replaced with https://vueuse.org/core/useVModel/
 */
export function useVModel<T extends Record<string, unknown>>(props: T, emit: VueEmit) {
  const modelValue = computed({
    get() {
      return props.modelValue
    },
    set(value) {
      emit('update:modelValue', value)
    },
  })
  return {
    modelValue,
  }
}

export function useFocusUserInputRef(inputSelector?: string) {
  const userInputRef = ref(null)
  const focus = () => {
    if (userInputRef.value) {
      const $el = userInputRef.value.$el || userInputRef.value
      if ($el) {
        if (inputSelector) {
          $el.querySelector(inputSelector)?.focus()
        } else {
          $el?.focus()
        }
      }
    }
  }

  return {
    userInputRef,
    focus,
  }
}
